/* Open-source licences dialog.
 *
 * This is not decoration and it is not optional. The charting library is
 * Apache-2.0 and its licence requires that an attribution notice AND a link to
 * the vendor appear somewhere in the application that users can reach. The
 * library offers an on-chart logo to satisfy that; Qorvex switches the logo off
 * to keep third-party branding off the trading surface, which means the
 * requirement has to be met HERE instead.
 *
 * So: do not delete this dialog or unlink it from the header without replacing
 * the charting library. Removing an attribution does not remove the obligation.
 *
 * The full set of notices lives in THIRD-PARTY-NOTICES.md at the repository
 * root; this shows the ones carrying a user-visible requirement.
 */
import { useEffect } from 'react'

interface Notice {
  name: string
  copyright: string
  licence: string
  /** Present when the licence requires a user-reachable link. */
  url?: string
  note?: string
}

const NOTICES: Notice[] = [
  {
    name: 'Lightweight Charts™',
    copyright: 'Copyright © 2025 TradingView, Inc.',
    licence: 'Apache License 2.0',
    url: 'https://www.tradingview.com/',
    note:
      'The charting engine behind the price chart. Its licence requires this ' +
      'attribution and link to be reachable from the application.',
  },
  { name: 'React · React DOM', copyright: 'Copyright © Meta Platforms, Inc.', licence: 'MIT' },
  { name: 'Zustand', copyright: 'Copyright © 2019 Paul Henschel', licence: 'MIT' },
  { name: 'React Router', copyright: 'Copyright © React Training / Remix Software', licence: 'MIT' },
  { name: 'FastAPI · Starlette · Pydantic', copyright: 'Copyright © Sebastián Ramírez and contributors', licence: 'MIT' },
  { name: 'SQLAlchemy · asyncpg · uvicorn', copyright: 'Respective authors', licence: 'MIT / BSD-3-Clause' },
  { name: 'PostgreSQL', copyright: 'Copyright © 1996–2026 The PostgreSQL Global Development Group', licence: 'PostgreSQL Licence' },
]

export function Licences({ open, onClose }: { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Open-source licences"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.55)',
        display: 'grid', placeItems: 'center', padding: 'var(--sp-4)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(640px, 100%)', maxHeight: '80vh', overflow: 'auto',
          background: 'var(--bg-panel)', border: '1px solid var(--border-strong)',
          borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
        }}
      >
        <header style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--sp-3) var(--sp-4)',
          borderBottom: '1px solid var(--border)', background: 'var(--bg-raised)',
          position: 'sticky', top: 0,
        }}>
          <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 700 }}>
            Open-source licences
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', color: 'var(--text-muted)',
              cursor: 'pointer', fontSize: 'var(--fs-lg)', lineHeight: 1,
              fontFamily: 'inherit',
            }}
          >
            ×
          </button>
        </header>

        <div style={{ padding: 'var(--sp-4)' }}>
          <p style={{
            fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)',
            marginBottom: 'var(--sp-4)', lineHeight: 1.6,
          }}>
            Qorvex is built on the open-source software below. Notices marked
            with a link are reproduced because their licence requires it.
          </p>

          {NOTICES.map((n) => (
            <div key={n.name} style={{
              paddingBottom: 'var(--sp-3)', marginBottom: 'var(--sp-3)',
              borderBottom: '1px solid var(--border)',
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'baseline', gap: 'var(--sp-3)', flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
                  {n.name}
                </span>
                <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                  {n.licence}
                </span>
              </div>
              <div style={{
                fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginTop: 2,
              }}>
                {n.copyright}
              </div>
              {n.note && (
                <div style={{
                  fontSize: 'var(--fs-2xs)', color: 'var(--text-secondary)',
                  marginTop: 4, lineHeight: 1.5,
                }}>
                  {n.note}
                </div>
              )}
              {n.url && (
                <a
                  href={n.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{
                    fontSize: 'var(--fs-2xs)', color: 'var(--accent)',
                    display: 'inline-block', marginTop: 4,
                  }}
                >
                  {n.url}
                </a>
              )}
            </div>
          ))}

          <p style={{
            fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', lineHeight: 1.6,
          }}>
            Full licence texts are distributed with each package, and the
            complete notice list is in THIRD-PARTY-NOTICES.md.
          </p>
        </div>
      </div>
    </div>
  )
}
